import { DestroyRef, inject, Injectable } from '@angular/core';

import { combineLatest, Observable } from 'rxjs';
import { map, skipWhile, switchMap, withLatestFrom } from 'rxjs/operators';

import { LogService } from '@innovation-os-ng/core/logger';
import { AuthContextSelectors, AuthContextState } from '@innovation-os-ng/core/state';
import { Store } from '@ngrx/store';
import { PermissionService } from '../contracts/permission.service';
import { PermissionVerbsV1 } from '../enums/PermissionVerbsV1';
import { PermissionVerbsV2 } from '../enums/PermissionVerbsV2';
import { BooleanOperator } from '../enums/boolean-operator';

export class PermissionContextMissingException {}

/**
 * Provides capabilities to check if the current user is allowed to
 * perform a certain action in the current space or tenant as well as
 * arbitrary space or tenant
 *
 * Notes Apis working:
 * 1. If we do not get spaceUri and tenantUri it is assumed that the permission
 * stream is asked for current context (selected tenant and space) or else
 *
 * 2. If we get both spaceUri and tenantUri we provide permission stream for the
 * asked spaceUri and tenantUri.
 *
 * 3. If we get spaceUri only we provide the permission stream for the asked space
 *
 * 4. If we get tenantUri only we provide the permission stream for the asked tenant
 */

@Injectable()
export class ApiPermissionService implements PermissionService {
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly loggerService: LogService,
    private readonly store: Store
  ) {}

  /**
   * Returns an Observable stream emitting whether the current user is allowed to
   * perform all the specified actions.
   *
   * @param actions List of actions to check for
   * @param spaceUri space uri to check actions for
   * @param tenantUri tenant uri to check actions for
   */
  public userHasPermissions(
    actions: Array<PermissionVerbsV1 | PermissionVerbsV2 | string>,
    spaceUri?: string,
    tenantUri?: string
  ): Observable<boolean> {
    const eventEmitters = actions.map((action) => this.userHasPermission(action, spaceUri, tenantUri));
    return combineLatest(eventEmitters).pipe(map((results) => undefined === results.find((x) => !x)));
  }

  /**
   * Returns an Observable stream emitting whether the current user is allowed to
   * perform at least one of the specified actions.
   *
   * @param actions List of actions to check for
   * @param spaceUri space uri to check actions for
   * @param tenantUri tenant uri to check actions for
   */
  public userHasAnyPermission(
    actions: Array<PermissionVerbsV1 | PermissionVerbsV2 | string>,
    spaceUri?: string,
    tenantUri?: string
  ): Observable<boolean> {
    const eventEmitters = actions.map((action) => this.userHasPermission(action, spaceUri, tenantUri));
    return combineLatest(eventEmitters).pipe(map((results) => !!results.find((x) => x)));
  }

  /**
   * Returns an Observable stream emitting whether the current user is allowed to
   * perform the given action.
   *
   *
   * @param action The action to check for
   * @param spaceUri space uri to check actions for
   * @param tenantUri tenant uri to check actions for
   */
  public userHasPermission(
    action: PermissionVerbsV1 | PermissionVerbsV2 | string,
    spaceUri?: string,
    tenantUri?: string
  ): Observable<boolean> {
    if (!spaceUri && !tenantUri) {
      return this.getCurrentContextObservable(action);
    }
    return this.getActions(tenantUri, spaceUri).pipe(
      map((results) => {
        if (!results) {
          this.loggerService.warn('Could not get actions', { tenantUri, spaceUri });
        }
        return results.indexOf(action) !== -1;
      })
    );
  }

  /**
   * Returns an Observable stream emitting whether the current user is allowed to
   * perform at least one of the specified actions in the element type.
   *
   * @param actions List of actions to check for
   * @param elementTypeUri elementType uri to check actions for
   * @param spaceUri space uri to check actions for
   * @returns
   */
  public userHasAnyElementTypePermissions(
    actions: Array<PermissionVerbsV1 | PermissionVerbsV2 | string>,
    elementTypeUri: string,
    spaceUri?: string
  ): Observable<boolean> {
    const permissionsObs: Array<Observable<boolean>> = actions.map((action) =>
      this.userHasElementTypePermission(action, elementTypeUri, spaceUri)
    );
    return combineLatest(permissionsObs).pipe(map((results) => !!results.find((x) => x)));
  }

  /**
   * Returns an Observable stream emitting whether the current user is allowed to
   * perform all the specified actions in the element type.
   *
   * @param actions List of actions to check for
   * @param elementTypeUri elementType uri to check actions for
   * @param spaceUri space uri to check actions for
   * @returns
   */
  public userHasElementTypePermissions(
    actions: Array<PermissionVerbsV1 | PermissionVerbsV2 | string>,
    elementTypeUri: string,
    spaceUri?: string
  ): Observable<boolean> {
    const permissionsObs: Array<Observable<boolean>> = actions.map((action) =>
      this.userHasElementTypePermission(action, elementTypeUri, spaceUri)
    );
    return combineLatest(permissionsObs).pipe(map((results) => !results.includes(false)));
  }

  /**
   *
   * @param action The action to check for
   * @param elementTypeUri elementType uri to check actions for
   * @param spaceUri space uri to check actions for
   * @returns
   */
  public userHasElementTypePermission(
    action: PermissionVerbsV1 | PermissionVerbsV2 | string,
    elementTypeUri: string,
    spaceUri?: string
  ): Observable<boolean> {
    if (!spaceUri) {
      return this.getCurrentContextElementTypeObservable(action, elementTypeUri);
    }

    return this.getActions(undefined, spaceUri, elementTypeUri).pipe(map((results) => !!results.find((result) => result === action)));
  }

  /**
   * Returns an Observable stream emitting whether the current user is allowed to
   * perform specified action in either tenant level or space level or in any one of the element type 
   *
   * @param action Action to check for
  
   */
  public checkPermissionTenantSpaceAnyType(action: PermissionVerbsV1 | PermissionVerbsV2 | string): Observable<boolean> {
    return this.combineAllLevelActions().pipe(
      map((results) => {
        if (!results) {
          this.loggerService.warn('Could not get actions');
        }
        return results.indexOf(action) !== -1;
      })
    );
  }

  /**
   * Returns an Observable stream emitting whether the current user is allowed to
   * perform all/any the specified actions in either tenant level or space level or in any one of the element type
   *
   * @param actions List of actions to check for
   * @param mode Boolean operator indicating wether to check all or any permissions
   * @returns
   */
  public checkPermissionsTenantSpaceAnyType(
    actions: Array<PermissionVerbsV1 | PermissionVerbsV2 | string>,
    mode: BooleanOperator
  ): Observable<boolean> {
    const permissionsObs: Array<Observable<boolean>> = actions.map((action) => this.checkPermissionTenantSpaceAnyType(action));
    return combineLatest(permissionsObs).pipe(
      map((results) => {
        if (mode === BooleanOperator.AND) {
          return !results.includes(false);
        } else if (mode === BooleanOperator.OR) {
          return !!results.find((x) => x);
        }
      })
    );
  }

  /**
   * Returns an Observable stream emitting whether the current user is allowed to
   * perform the given action for provided element type uri
   * new notification to the permission stream !!
   *
   * @param action The action to check for
   * @param elementTypeUri element type uri to check
   */
  private getCurrentContextElementTypeObservable(action: string, elementTypeUri: string): Observable<boolean> {
    return this.store
      .select(AuthContextSelectors.selectCurrentSpaceElementTypeActions(elementTypeUri))
      .pipe(withLatestFrom(this.store.select(AuthContextSelectors.selectCurrentPermissions)))
      .pipe(
        map(([elementTypeActions, { spaceActions, tenantActions }]) => {
          return (elementTypeActions || spaceActions || []).indexOf(action) !== -1;
        })
      );
  }

  /**
   * Returns an Observable stream emitting whether the current user is allowed to
   * perform the given action for current space and tenant which emits
   * new notification to the permission stream !!
   *
   * @param action The action to check for
   */
  private getCurrentContextObservable(action: PermissionVerbsV1 | PermissionVerbsV2 | string): Observable<boolean> {
    return this.store.select(AuthContextSelectors.selectCurrentPermissions).pipe(
      skipWhile((permissions) => {
        return (
          !permissions.spaceActions || !permissions.tenantActions || !permissions.spaceActions.length || !permissions.tenantActions.length
        );
      }),
      // eslint-disable-next-line @ngrx/avoid-mapping-selectors
      map(({ spaceActions, tenantActions }) => {
        return [...(spaceActions || []), ...(tenantActions || [])].indexOf(action) !== -1;
      })
    );
  }

  /**
   * This returns combination observable stream for tenant and space actions or
   * each individual one
   *
   * @param tenantUri tenant uri which is cached
   * @param spaceUri space uri which is cached
   * @param elementTypeUri element type uri which is cached
   */
  private getActions(tenantUri?: string, spaceUri?: string, elementTypeUri?: string): Observable<Array<string>> {
    if (!tenantUri && !spaceUri) {
      this.loggerService.error('Permissions atleast needs a context either space or tenant or both: None of it is obtained !!');
      throw new PermissionContextMissingException();
    }

    return this.store.select(AuthContextSelectors.selectAuthContext).pipe(
      skipWhile((authCtx: AuthContextState) => {
        return authCtx.isLoading;
      }),
      switchMap(() => {
        return this.store
          .select(AuthContextSelectors.selectSpaceActions(spaceUri))
          .pipe(
            withLatestFrom(
              this.store.select(AuthContextSelectors.selectTenantsActions(tenantUri)),
              this.store.select(AuthContextSelectors.selectElementTypeActions(spaceUri, elementTypeUri))
            )
          )
          .pipe(
            map(([spaceActions, tenantActions, elementTypeActions]: [Array<string>, Array<string>, Array<string>]) => {
              // If we have element type passed, then it indicate element type specific permission
              if (elementTypeUri) {
                return elementTypeActions || spaceActions || [];
              }

              if (tenantUri && spaceUri) {
                return [...(tenantActions || []), ...(spaceActions || [])];
              } else if (tenantUri) {
                return tenantActions || [];
              } else if (spaceUri) {
                return spaceActions || [];
              }
            })
          );
      })
    );
  }

  /**
   * Return list of actions combining action from tenant, space and element type level
   *
   * @returns
   */
  private combineAllLevelActions(): Observable<Array<string>> {
    return this.store.select(AuthContextSelectors.selectAuthContext).pipe(
      skipWhile((authCtx: AuthContextState) => {
        return authCtx.isLoading;
      }),
      switchMap(() => {
        return this.store
          .select(AuthContextSelectors.selectCurrentSpaceActions)
          .pipe(skipWhile((actions) => !actions))
          .pipe(
            withLatestFrom(
              this.store.select(AuthContextSelectors.selectCurrentTenantActions),
              this.store.select(AuthContextSelectors.selectCurrentSpaceCombinedElementTypeActions)
            )
          )
          .pipe(
            map(([spacePermissions, tenantPermissions, allElementTypeActions]) => {
              return [...(tenantPermissions || []), ...(spacePermissions || []), ...(allElementTypeActions || [])];
            })
          );
      })
    );
  }
}
